import { Dispatch, ReactElement, SetStateAction, useState } from 'react';
import { faUserAlt, faPencilAlt, faSync, faPencil } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { styled, useMediaQuery, Button, Tooltip } from '@mui/material';
import styles from '../common/styles/util.module.scss';
import {
  ExpandButton,
  fullName,
  IUser,
  variables,
  viewUserProfile,
  logoutOkta,
  useDeaRegistrations,
  PDFModal,
} from '../common';

export function ViewUserProfile({
  user,
  edit,
  details,
  setEdit,
  hasActiveRequests,
}: {
  user: IUser;
  edit: boolean;
  details: () => ReactElement;
  setEdit: Dispatch<SetStateAction<boolean>>;
  hasActiveRequests: boolean;
}) {
  const [expand, setExpand] = useState(true);

  const matchesGridBreakpoint = useMediaQuery('(min-width:60em)', {
    noSsr: true,
  });

  const toggleExpand = () => setExpand(x => !x);

  // Name Change Handler
  const handleNameChange = () => {
    // Generate unique state for name change flow
    const nameChangeState = `nameChange_${Date.now()}_${Math.random().toString(36).substring(7)}`;

    // Build Login.gov URL with the nameChange state
    let loginGovUrl = (import.meta as any).env.VITE_SIGNUP_URL;
    loginGovUrl = loginGovUrl.replace('state=123456789012345678901234567890', `state=${nameChangeState}`);

    // Logout after 3 seconds 
    setTimeout(logoutOkta, 3_000);

    // Redirect to Login.gov for name change
    window.location.href = loginGovUrl;
  };

  // Name Sync Handler
  const handleNameSync = () => {
    // Generate unique state for name sync flow
    const nameSyncState = `nameSync_${Date.now()}_${Math.random().toString(36).substring(7)}`;

    // Build Login.gov URL with the nameSync state
    let loginGovUrl = (import.meta as any).env.VITE_SIGNUP_URL;
    loginGovUrl = loginGovUrl.replace('state=123456789012345678901234567890', `state=${nameSyncState}`);

    // Logout after 3 seconds
    setTimeout(logoutOkta, 3_000);

    // Redirect to Login.gov for name sync
    window.location.href = loginGovUrl;
  };

  const { deaRegistrations } = useDeaRegistrations();
  const nameSyncReady = !hasActiveRequests && (deaRegistrations.length === 0);

  return (
    <Wrapper>
      <Header>
        <h2>
          <FontAwesomeIcon icon={faUserAlt} size="1x" fixedWidth />
          <span>{fullName(user)}</span>
        </h2>

        <ButtonGroup>
          {viewUserProfile(user)(edit) && (
            <div style={{ marginBottom: '5px'}}>
              <Button
                onClick={() => setEdit(true)}>
                Edit
                <FontAwesomeIcon icon={faPencilAlt} size="1x" fixedWidth />
              </Button>
              <PDFModal
                fileUrl={'Name Change Instructions.pdf'}
                fileLabel={'Name change instructions'}
                fileDownload="name_change_instructions.pdf"
              />
            </div>
          )}

          {edit &&
            <>
              <NameButtonWrapper>
                {/* <PDFModal
                  fileUrl={'Name Change Instructions.pdf'}
                  fileLabel={'Name change instructions'}
                  fileDownload="name_change_instructions.pdf"
                /> */}
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleNameChange}
                  style={{ marginLeft: '3px' }}
                >
                  Change Name in Login.gov
                  <FontAwesomeIcon icon={faPencil} size="1x" fixedWidth />
                </Button>

                <Tooltip
                  title={!nameSyncReady ? 'Must not have any active certificates or requests' : ''}
                  arrow>
                  <span> {/* Wrapper needed for Tooltip to work on disabled button */}
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleNameSync}
                      disabled={!nameSyncReady}>
                      Sync Name from Login.gov
                      <FontAwesomeIcon icon={faSync} size="1x" fixedWidth />
                    </Button>
                  </span>
                </Tooltip>
              </NameButtonWrapper>
            </>}

        </ButtonGroup>
        <ExpandButton
          onClick={toggleExpand}
          value={expand}
          matchesGridBreakpoint={matchesGridBreakpoint}
        />
      </Header>
      {expand && (
        <>
          <Person>{details()}</Person>
        </>
      )}
    </Wrapper>
  );
}

const Wrapper = styled('article')(({ theme }) => ({
  display: 'grid',
  gridTemplateAreas: `
    "header"
    "person"
    `,
  gap: '2rem',

  '@media (min-width: 800px)': {
    gridTemplateColumns: '2fr 1fr',
    gridTemplateAreas: `
    "header header "
    "person person"
    `,
  },
}));

const Header = styled('header')(({ theme }) => ({
  gridArea: 'header',

  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',

  borderBottom: variables.basicBorder,
}));

const ButtonGroup = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: '0.5rem',
  marginRight: 'auto',
  marginLeft: '1rem',

  '@media (max-width: 800px)': {
    flexDirection: 'column',
    gap: '0.35rem',
  },
}));

const Person = styled('article')(({ theme }) => ({
  gridArea: 'person',

  '&>*+*': {
    marginTop: '0.5rem',
  },
}));

const NameButtonWrapper = styled('div')({
  marginBottom: '0.5rem',
});
